import { useState } from "react";
import { Check, Copy } from "lucide-react";
import Button from "../Button";
import { useToast } from "../../../_context/ToastContext/ToastContext";

/**
 * Copy-to-clipboard button - icon swaps to a checkmark and (unless
 * `iconOnly`) the label swaps to `copiedLabel` for 2s after a successful
 * copy, plus a toast either way. Was hand-rolled independently 3 times
 * (webhooks' CopyableField, the usage-source API-link panel's "Copy curl",
 * self-serve checkout's per-step "Copy code") with the same
 * navigator.clipboard.writeText + try/catch + toast + timed reset shape.
 *
 * `value` covers a static string; pass `getValue` instead when the text to
 * copy must be computed fresh at click time (e.g. a curl command whose
 * timestamp changes per copy) - it may return a string or a Promise<string>.
 */
const CopyButton = ({
  value,
  getValue,
  iconOnly = false,
  label = "Copy",
  copiedLabel = "Copied!",
  successMessage = "Copied to clipboard successfully!",
  errorMessage = "Failed to copy to clipboard. Please try again.",
  variant = iconOnly ? "ghost" : "outlined",
  size = "sm",
  disabled = false,
  title = "Copy to clipboard",
  className = "",
  ...rest
}) => {
  const { showToast } = useToast();
  const [justCopied, setJustCopied] = useState(false);

  const handleCopy = async () => {
    const textToCopy = getValue ? await getValue() : value;
    if (!textToCopy) return;

    try {
      await navigator.clipboard.writeText(textToCopy);
      showToast(successMessage, "success");
      setJustCopied(true);
      setTimeout(() => setJustCopied(false), 2000);
    } catch {
      showToast(errorMessage, "error");
    }
  };

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      iconOnly={iconOnly}
      className={className}
      onClick={handleCopy}
      disabled={disabled || (!value && !getValue)}
      title={iconOnly ? title : undefined}
      icon={justCopied ? <Check size={14} /> : <Copy size={14} />}
      {...rest}
    >
      {!iconOnly && (justCopied ? copiedLabel : label)}
    </Button>
  );
};

export default CopyButton;
